import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const themes = {
  blue: {
    name: 'Ocean Blue',
    gradient: 'linear-gradient(140deg, #312e81 0%, #4338ca 30%, #4f46e5 55%, #0ea5e9 100%)',
    primaryColor: '#4f46e5',
    primaryDark: '#4338ca',
    secondaryColor: '#0ea5e9',
    textColor: '#ffffff',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#0f172a',
    surfaceMuted: '#475569',
    surfaceBorder: 'rgba(79, 70, 229, 0.25)',
    bodyBg: '#eef2ff',
    bodyText: '#0f172a',
    chipBg: 'rgba(255, 255, 255, 0.22)',
    chipText: '#f8fafc',
    buttonText: '#ffffff',
    buttonShadow: '0 18px 45px -20px rgba(79, 70, 229, 0.85)',
    cardShadow: '0 30px 90px -35px rgba(15, 23, 42, 0.45)'
  },
  green: {
    name: 'Sunset Bloom',
    gradient: 'linear-gradient(140deg, #ff9a8b 0%, #fecfef 56%, #fe99c3 100%)',
    primaryColor: '#fb7185',
    primaryDark: '#e11d48',
    secondaryColor: '#f9a8d4',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.98)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(249, 168, 212, 0.35)',
    bodyBg: '#fff1f2',
    bodyText: '#1f2937',
    chipBg: 'rgba(255, 255, 255, 0.4)',
    chipText: '#db2777',
    buttonText: '#ffffff',
    buttonShadow: '0 18px 45px -20px rgba(219, 39, 119, 0.65)',
    cardShadow: '0 35px 100px -45px rgba(219, 39, 119, 0.35)'
  },
  mixed: {
    name: 'Sky Mix',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    primaryColor: '#0ea5e9',
    primaryDark: '#0284c7',
    secondaryColor: '#22d3ee',
    textColor: '#ffffff',
    surface: 'rgba(255, 255, 255, 0.96)',
    surfaceText: '#0f172a',
    surfaceMuted: '#475569',
    surfaceBorder: 'rgba(14, 165, 233, 0.3)',
    bodyBg: '#e0f2fe',
    bodyText: '#0f172a',
    chipBg: 'rgba(255, 255, 255, 0.3)',
    chipText: '#0f172a',
    buttonText: '#ffffff',
    buttonShadow: '0 20px 50px -30px rgba(14, 165, 233, 0.85)',
    cardShadow: '0 25px 80px -40px rgba(2, 132, 199, 0.4)'
  },
  soft: {
    name: 'Soft Pastel',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    primaryColor: '#ec4899',
    primaryDark: '#db2777',
    secondaryColor: '#f472b6',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.98)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(236, 72, 153, 0.2)',
    bodyBg: '#fff7fb',
    bodyText: '#1f2937',
    chipBg: 'rgba(255, 255, 255, 0.5)',
    chipText: '#db2777',
    buttonText: '#ffffff',
    buttonShadow: '0 20px 55px -30px rgba(236, 72, 153, 0.55)',
    cardShadow: '0 30px 95px -55px rgba(236, 72, 153, 0.45)'
  },
  minimal: {
    name: 'Minimal Mist',
    gradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    primaryColor: '#0f172a',
    primaryDark: '#020617',
    secondaryColor: '#475569',
    textColor: '#0f172a',
    surface: '#ffffff',
    surfaceText: '#111827',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(148, 163, 184, 0.35)',
    bodyBg: '#f8fafc',
    bodyText: '#0f172a',
    chipBg: 'rgba(15, 23, 42, 0.08)',
    chipText: '#0f172a',
    buttonText: '#ffffff',
    buttonShadow: '0 20px 40px -25px rgba(15, 23, 42, 0.6)',
    cardShadow: '0 25px 90px -45px rgba(15, 23, 42, 0.25)'
  },
  dark: {
    name: 'Night Mode',
    gradient: 'linear-gradient(135deg, #0f172a 0%, #111827 55%, #020617 100%)',
    primaryColor: '#6366f1',
    primaryDark: '#4c1d95',
    secondaryColor: '#a855f7',
    textColor: '#f8fafc',
    surface: 'rgba(15, 23, 42, 0.85)',
    surfaceText: '#f8fafc',
    surfaceMuted: '#94a3b8',
    surfaceBorder: 'rgba(255, 255, 255, 0.12)',
    bodyBg: '#020617',
    bodyText: '#f8fafc',
    chipBg: 'rgba(248, 250, 252, 0.15)',
    chipText: '#c084fc',
    buttonText: '#f8fafc',
    buttonShadow: '0 25px 60px -30px rgba(99, 102, 241, 0.65)',
    cardShadow: '0 35px 120px -40px rgba(15, 15, 36, 0.9)'
  },
  coral: {
    name: 'Coral Pop',
    gradient: 'linear-gradient(140deg, #ff7a7a 0%, #ffb347 50%, #ffd166 100%)',
    primaryColor: '#f97316',
    primaryDark: '#ea580c',
    secondaryColor: '#facc15',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.98)',
    surfaceText: '#111827',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(249, 115, 22, 0.25)',
    bodyBg: '#fff7ed',
    bodyText: '#111827',
    chipBg: 'rgba(255, 255, 255, 0.45)',
    chipText: '#c2410c',
    buttonText: '#ffffff',
    buttonShadow: '0 24px 60px -30px rgba(249, 115, 22, 0.6)',
    cardShadow: '0 30px 100px -50px rgba(234, 88, 12, 0.35)'
  },
  forest: {
    name: 'Forest Meadow',
    gradient: 'linear-gradient(135deg, #0f9b0f 0%, #007a3d 40%, #38b2ac 100%)',
    primaryColor: '#047857',
    primaryDark: '#065f46',
    secondaryColor: '#10b981',
    textColor: '#0f172a',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#0f172a',
    surfaceMuted: '#4b5563',
    surfaceBorder: 'rgba(16, 185, 129, 0.25)',
    bodyBg: '#ecfdf3',
    bodyText: '#0f172a',
    chipBg: 'rgba(16, 185, 129, 0.14)',
    chipText: '#065f46',
    buttonText: '#ffffff',
    buttonShadow: '0 22px 60px -30px rgba(16, 185, 129, 0.55)',
    cardShadow: '0 32px 100px -52px rgba(4, 120, 87, 0.35)'
  },
  aurora: {
    name: 'Aurora Night',
    gradient: 'linear-gradient(135deg, #111827 0%, #312e81 40%, #22d3ee 100%)',
    primaryColor: '#22d3ee',
    primaryDark: '#0ea5e9',
    secondaryColor: '#a855f7',
    textColor: '#e2e8f0',
    surface: 'rgba(15, 23, 42, 0.85)',
    surfaceText: '#e2e8f0',
    surfaceMuted: '#94a3b8',
    surfaceBorder: 'rgba(34, 211, 238, 0.25)',
    bodyBg: '#0b1224',
    bodyText: '#e2e8f0',
    chipBg: 'rgba(255, 255, 255, 0.14)',
    chipText: '#22d3ee',
    buttonText: '#0b1224',
    buttonShadow: '0 26px 80px -40px rgba(34, 211, 238, 0.55)',
    cardShadow: '0 32px 110px -45px rgba(12, 20, 38, 0.85)'
  },
  sandstone: {
    name: 'Sandstone',
    gradient: 'linear-gradient(140deg, #f2e8cf 0%, #e8d0a9 45%, #d9a05b 100%)',
    primaryColor: '#d97706',
    primaryDark: '#b45309',
    secondaryColor: '#f59e0b',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.96)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(217, 119, 6, 0.25)',
    bodyBg: '#fffbeb',
    bodyText: '#1f2937',
    chipBg: 'rgba(255, 255, 255, 0.5)',
    chipText: '#92400e',
    buttonText: '#ffffff',
    buttonShadow: '0 24px 70px -35px rgba(217, 119, 6, 0.5)',
    cardShadow: '0 32px 100px -50px rgba(148, 81, 0, 0.3)'
  },
  neon: {
    name: 'Neon Grid',
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 40%, #22d3ee 80%, #14b8a6 100%)',
    primaryColor: '#22d3ee',
    primaryDark: '#0ea5e9',
    secondaryColor: '#10b981',
    textColor: '#e2e8f0',
    surface: 'rgba(15, 23, 42, 0.9)',
    surfaceText: '#e2e8f0',
    surfaceMuted: '#94a3b8',
    surfaceBorder: 'rgba(99, 102, 241, 0.25)',
    bodyBg: '#0b1224',
    bodyText: '#e2e8f0',
    chipBg: 'rgba(34, 211, 238, 0.15)',
    chipText: '#22d3ee',
    buttonText: '#0b1224',
    buttonShadow: '0 26px 80px -40px rgba(99, 102, 241, 0.6)',
    cardShadow: '0 32px 110px -45px rgba(12, 20, 38, 0.85)'
  },
  berry: {
    name: 'Berry Crush',
    gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 45%, #ec4899 100%)',
    primaryColor: '#7c3aed',
    primaryDark: '#4c1d95',
    secondaryColor: '#ec4899',
    textColor: '#f8fafc',
    surface: 'rgba(255, 255, 255, 0.96)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(124, 58, 237, 0.25)',
    bodyBg: '#fdf2f8',
    bodyText: '#1f2937',
    chipBg: 'rgba(124, 58, 237, 0.1)',
    chipText: '#4c1d95',
    buttonText: '#ffffff',
    buttonShadow: '0 26px 80px -40px rgba(124, 58, 237, 0.6)',
    cardShadow: '0 32px 110px -50px rgba(76, 29, 149, 0.35)'
  },
  slate: {
    name: 'Slate Mono',
    gradient: 'linear-gradient(140deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    primaryColor: '#334155',
    primaryDark: '#0f172a',
    secondaryColor: '#475569',
    textColor: '#e2e8f0',
    surface: 'rgba(17, 24, 39, 0.92)',
    surfaceText: '#e2e8f0',
    surfaceMuted: '#94a3b8',
    surfaceBorder: 'rgba(226, 232, 240, 0.1)',
    bodyBg: '#0b1224',
    bodyText: '#e2e8f0',
    chipBg: 'rgba(51, 65, 85, 0.4)',
    chipText: '#e2e8f0',
    buttonText: '#e2e8f0',
    buttonShadow: '0 20px 60px -35px rgba(15, 23, 42, 0.9)',
    cardShadow: '0 28px 100px -45px rgba(15, 23, 42, 0.8)'
  },
  sunrise: {
    name: 'Sunrise',
    gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 50%, #fad0c4 100%)',
    primaryColor: '#f97316',
    primaryDark: '#ea580c',
    secondaryColor: '#f59e0b',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(234, 88, 12, 0.25)',
    bodyBg: '#fff7ed',
    bodyText: '#1f2937',
    chipBg: 'rgba(255, 255, 255, 0.5)',
    chipText: '#c2410c',
    buttonText: '#ffffff',
    buttonShadow: '0 24px 70px -35px rgba(234, 88, 12, 0.45)',
    cardShadow: '0 30px 95px -50px rgba(234, 88, 12, 0.35)'
  },
  teal: {
    name: 'Teal Tide',
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 50%, #6366f1 100%)',
    primaryColor: '#0ea5e9',
    primaryDark: '#0284c7',
    secondaryColor: '#14b8a6',
    textColor: '#0f172a',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#0f172a',
    surfaceMuted: '#475569',
    surfaceBorder: 'rgba(20, 184, 166, 0.25)',
    bodyBg: '#ecfeff',
    bodyText: '#0f172a',
    chipBg: 'rgba(14, 165, 233, 0.12)',
    chipText: '#0f172a',
    buttonText: '#ffffff',
    buttonShadow: '0 22px 70px -35px rgba(14, 165, 233, 0.6)',
    cardShadow: '0 30px 95px -45px rgba(14, 165, 233, 0.35)'
  },
  violet: {
    name: 'Violet Dream',
    gradient: 'linear-gradient(135deg, #a855f7 0%, #6366f1 50%, #22d3ee 100%)',
    primaryColor: '#7c3aed',
    primaryDark: '#5b21b6',
    secondaryColor: '#22d3ee',
    textColor: '#f8fafc',
    surface: 'rgba(255, 255, 255, 0.95)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(99, 102, 241, 0.25)',
    bodyBg: '#f5f3ff',
    bodyText: '#1f2937',
    chipBg: 'rgba(124, 58, 237, 0.1)',
    chipText: '#5b21b6',
    buttonText: '#ffffff',
    buttonShadow: '0 26px 80px -40px rgba(124, 58, 237, 0.6)',
    cardShadow: '0 30px 100px -50px rgba(99, 102, 241, 0.4)'
  },
  citrus: {
    name: 'Citrus Burst',
    gradient: 'linear-gradient(135deg, #fbbf24 0%, #f97316 45%, #ef4444 100%)',
    primaryColor: '#f97316',
    primaryDark: '#c2410c',
    secondaryColor: '#fbbf24',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(249, 115, 22, 0.25)',
    bodyBg: '#fff7ed',
    bodyText: '#1f2937',
    chipBg: 'rgba(249, 115, 22, 0.12)',
    chipText: '#9a3412',
    buttonText: '#ffffff',
    buttonShadow: '0 24px 70px -35px rgba(249, 115, 22, 0.5)',
    cardShadow: '0 30px 95px -50px rgba(239, 68, 68, 0.3)'
  },
  cobalt: {
    name: 'Cobalt Steel',
    gradient: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%)',
    primaryColor: '#2563eb',
    primaryDark: '#1d4ed8',
    secondaryColor: '#38bdf8',
    textColor: '#e2e8f0',
    surface: 'rgba(15, 23, 42, 0.9)',
    surfaceText: '#e2e8f0',
    surfaceMuted: '#94a3b8',
    surfaceBorder: 'rgba(37, 99, 235, 0.25)',
    bodyBg: '#0b1224',
    bodyText: '#e2e8f0',
    chipBg: 'rgba(37, 99, 235, 0.16)',
    chipText: '#e2e8f0',
    buttonText: '#0b1224',
    buttonShadow: '0 26px 80px -40px rgba(37, 99, 235, 0.55)',
    cardShadow: '0 32px 110px -45px rgba(12, 20, 38, 0.85)'
  },
  blush: {
    name: 'Blush',
    gradient: 'linear-gradient(135deg, #fda4af 0%, #f9a8d4 50%, #fdf2f8 100%)',
    primaryColor: '#ec4899',
    primaryDark: '#db2777',
    secondaryColor: '#f9a8d4',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.98)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(236, 72, 153, 0.25)',
    bodyBg: '#fff1f2',
    bodyText: '#1f2937',
    chipBg: 'rgba(236, 72, 153, 0.12)',
    chipText: '#9d174d',
    buttonText: '#ffffff',
    buttonShadow: '0 24px 70px -35px rgba(236, 72, 153, 0.5)',
    cardShadow: '0 30px 95px -50px rgba(236, 72, 153, 0.35)'
  },
  lagoon: {
    name: 'Lagoon',
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #22c55e 60%, #bef264 100%)',
    primaryColor: '#22c55e',
    primaryDark: '#15803d',
    secondaryColor: '#0ea5e9',
    textColor: '#0f172a',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#0f172a',
    surfaceMuted: '#475569',
    surfaceBorder: 'rgba(34, 197, 94, 0.25)',
    bodyBg: '#ecfeff',
    bodyText: '#0f172a',
    chipBg: 'rgba(34, 197, 94, 0.12)',
    chipText: '#166534',
    buttonText: '#ffffff',
    buttonShadow: '0 22px 70px -35px rgba(34, 197, 94, 0.55)',
    cardShadow: '0 30px 95px -45px rgba(14, 165, 233, 0.35)'
  },
  latte: {
    name: 'Latte',
    gradient: 'linear-gradient(140deg, #f7ead7 0%, #e0c9a6 50%, #c59b6c 100%)',
    primaryColor: '#b45309',
    primaryDark: '#92400e',
    secondaryColor: '#d97706',
    textColor: '#1f2937',
    surface: 'rgba(255, 255, 255, 0.97)',
    surfaceText: '#1f2937',
    surfaceMuted: '#6b7280',
    surfaceBorder: 'rgba(180, 83, 9, 0.22)',
    bodyBg: '#fff7ed',
    bodyText: '#1f2937',
    chipBg: 'rgba(255, 255, 255, 0.55)',
    chipText: '#92400e',
    buttonText: '#ffffff',
    buttonShadow: '0 24px 70px -35px rgba(180, 83, 9, 0.45)',
    cardShadow: '0 30px 95px -50px rgba(153, 92, 35, 0.3)'
  }
};

const appBackgroundOptions = [
  { id: 'default', label: 'Default', base: '#ffffff', blob1: null, blob2: null, avatarBg: '#f8fafc' },
  { id: 'sky', label: 'Sky', base: '#f0f9ff', blob1: '#7dd3fc', blob2: '#c7d2fe', avatarBg: '#e0f2fe' },
  { id: 'white', label: 'White', base: '#ffffff', blob1: '#e5e7eb', blob2: '#c7d2fe', avatarBg: '#f8fafc' },
  { id: 'sand', label: 'Sand', base: '#fdf6e3', blob1: '#fbbf24', blob2: '#fcd34d', avatarBg: '#fef3c7' },
  { id: 'slate', label: 'Slate', base: '#f8fafc', blob1: '#cbd5e1', blob2: '#e2e8f0', avatarBg: '#e2e8f0' },
  { id: 'mint', label: 'Mint', base: '#ecfdf3', blob1: '#34d399', blob2: '#a7f3d0', avatarBg: '#d1fae5' },
  { id: 'rose', label: 'Rose', base: '#fff1f2', blob1: '#fb7185', blob2: '#fecdd3', avatarBg: '#ffe4e6' },
  { id: 'dusk', label: 'Dusk', base: '#eef2ff', blob1: '#a78bfa', blob2: '#c7d2fe', avatarBg: '#e0e7ff' },
  { id: 'peach', label: 'Peach', base: '#fff7ed', blob1: '#fb923c', blob2: '#fed7aa', avatarBg: '#ffedd5' },
  { id: 'citrus', label: 'Citrus', base: '#fffbeb', blob1: '#fcd34d', blob2: '#f97316', avatarBg: '#fef9c3' },
  { id: 'seafoam', label: 'Seafoam', base: '#effdfd', blob1: '#67e8f9', blob2: '#22c55e', avatarBg: '#ccfbf1' },
  { id: 'lilac', label: 'Lilac', base: '#f5f3ff', blob1: '#c084fc', blob2: '#a78bfa', avatarBg: '#ede9fe' },
  { id: 'cobalt', label: 'Cobalt', base: '#e0f2fe', blob1: '#2563eb', blob2: '#38bdf8', avatarBg: '#bfdbfe' },
  { id: 'orchid', label: 'Orchid', base: '#faf5ff', blob1: '#c084fc', blob2: '#e879f9', avatarBg: '#f3e8ff' },
  { id: 'forest', label: 'Forest', base: '#ecfdf3', blob1: '#10b981', blob2: '#22c55e', avatarBg: '#d1fae5' },
  { id: 'denim', label: 'Denim', base: '#eef2ff', blob1: '#6366f1', blob2: '#0ea5e9', avatarBg: '#e0f2fe' },
  { id: 'cafe', label: 'Cafe', base: '#f5f5f4', blob1: '#d6d3d1', blob2: '#f59e0b', avatarBg: '#e7e5e4' },
  { id: 'ember', label: 'Ember', base: '#fff1f2', blob1: '#f97316', blob2: '#fb7185', avatarBg: '#ffe4e6' },
  { id: 'teal', label: 'Teal', base: '#ecfeff', blob1: '#06b6d4', blob2: '#22d3ee', avatarBg: '#cffafe' },
  { id: 'platinum', label: 'Platinum', base: '#f8fafc', blob1: '#e2e8f0', blob2: '#cbd5e1', avatarBg: '#e2e8f0' },
  { id: 'aurora', label: 'Aurora', base: '#0b1224', blob1: '#22d3ee', blob2: '#a855f7', avatarBg: '#1f2937' },
  { id: 'charcoal', label: 'Charcoal', base: '#111827', blob1: '#1f2937', blob2: '#374151', avatarBg: '#1f2937' },
  { id: 'midnight', label: 'Midnight', base: '#0f172a', blob1: '#1d4ed8', blob2: '#0ea5e9', avatarBg: '#1f2937' },
  { id: 'blush', label: 'Blush', base: '#fff1f2', blob1: '#f9a8d4', blob2: '#f472b6', avatarBg: '#ffe4e6' },
  { id: 'canyon', label: 'Canyon', base: '#fef3c7', blob1: '#f59e0b', blob2: '#fb923c', avatarBg: '#fde68a' }
];

export const useThemeStore = create(
  persist(
    (set) => ({
      currentTheme: 'blue',
      appBackgroundId: 'default',
      appBackgroundOptions,
      themes,
      
      setTheme: (themeName) => {
        if (themes[themeName]) {
          set({ currentTheme: themeName });
        }
      },

      setAppBackground: (idOrColor) => {
        const opt = appBackgroundOptions.find((o) => o.id === idOrColor);
        if (opt) {
          set({ appBackgroundId: opt.id });
        } else {
          // legacy direct color support
          set({ appBackgroundId: null, appBackground: idOrColor });
        }
      },

      getAppBackgroundOption: (id) => {
        return appBackgroundOptions.find((o) => o.id === id) || appBackgroundOptions[0];
      },
      
      getTheme: (themeName) => {
        return themes[themeName || 'blue'];
      },
      
      getCurrentThemeData: () => {
        const state = useThemeStore.getState();
        return themes[state.currentTheme];
      }
    }),
    {
      name: 'fomz-theme-storage',
      version: 2,
      migrate: (state) => {
        // Ensure we always ship the latest theme catalog
        return { ...state, themes };
      },
      partialize: (state) => ({
        currentTheme: state.currentTheme,
        appBackgroundId: state.appBackgroundId || 'default'
      })
    }
  )
);
